<template>
  <div class="condiciones-home">
    <div class="header">
      <div class="filters">
        <div class="filter-group">
          <label>Empleado</label>
          <select v-model="filtros.empleado">
            <option value="">Todos</option>
            <option v-for="emp in empleados" :key="emp.id" :value="emp.id">
              {{ emp.nombre }}
            </option>
          </select>
        </div>

        <label>
          <input v-model="filtros.vigentes" type="checkbox" />
          Solo vigentes
        </label>

        <button class="btn-primary" @click="abrirModalCrear">
          Nueva Condición
        </button>
      </div>
    </div>

    <div v-if="cargando" class="loading">Cargando...</div>

    <table v-else class="condiciones-table">
      <thead>
        <tr>
          <th>Empleado</th>
          <th>Modalidad</th>
          <th>Periodicidad</th>
          <th>Importe Fijo</th>
          <th>Valor Hora</th>
          <th>Vigencia</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="cond in condicionesFiltradas" :key="cond.id">
          <td>{{ cond.empleado_nombre }}</td>
          <td>{{ cond.modalidad_display }}</td>
          <td>{{ cond.periodicidad_display }}</td>
          <td class="numero">{{ formatearMoneda(cond.importe_fijo) }}</td>
          <td class="numero">{{ formatearMoneda(cond.valor_hora_efectivo) }}</td>
          <td>
            {{ formatearFecha(cond.vigencia_desde) }}
            <span v-if="cond.vigencia_hasta">- {{ formatearFecha(cond.vigencia_hasta) }}</span>
          </td>
          <td class="acciones">
            <button class="btn-link" @click="editarCondicion(cond.id)">Editar</button>
            <button class="btn-link" @click="eliminarCondicion(cond.id)">Eliminar</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modal crear/editar -->
    <div v-if="modalVisible" class="modal-overlay" @click="modalVisible = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h2>{{ editandoId ? 'Editar' : 'Nueva' }} Condición Laboral</h2>
          <button class="btn-close" @click="modalVisible = false">×</button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="guardarCondicion">
            <div class="form-group">
              <label>Empleado *</label>
              <select v-model="form.empleado" required :disabled="editandoId">
                <option value="">Seleccionar empleado</option>
                <option v-for="emp in empleados" :key="emp.id" :value="emp.id">
                  {{ emp.nombre }}
                </option>
              </select>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Modalidad *</label>
                <select v-model="form.modalidad" required>
                  <option value="HORA">Hora</option>
                  <option value="FIJO">Fijo</option>
                </select>
              </div>

              <div class="form-group">
                <label>Periodicidad *</label>
                <select v-model="form.periodicidad" required>
                  <option value="SEMANAL">Semanal</option>
                  <option value="QUINCENAL">Quincenal</option>
                  <option value="MENSUAL">Mensual</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label>Importe Fijo *</label>
              <input v-model.number="form.importe_fijo" type="number" step="0.01" required />
            </div>

            <div class="form-group">
              <label>Horas de Jornada Semanal</label>
              <input v-model.number="form.horas_jornada_semanal" type="number" />
            </div>

            <div class="form-group">
              <label>Valor Hora (opcional)</label>
              <input v-model.number="form.valor_hora" type="number" step="0.01" />
            </div>

            <div class="form-group">
              <label>Redondeo Múltiplo</label>
              <input v-model.number="form.redondeo_multiplo" type="number" />
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Vigencia Desde *</label>
                <input v-model="form.vigencia_desde" type="date" required />
              </div>

              <div class="form-group">
                <label>Vigencia Hasta</label>
                <input v-model="form.vigencia_hasta" type="date" />
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn-secondary" @click="modalVisible = false">
                Cancelar
              </button>
              <button type="submit" class="btn-primary" :disabled="cargandoForm">
                {{ cargandoForm ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal de error -->
    <div v-if="error" class="error-alert">
      <p>{{ error }}</p>
      <button @click="error = null">Cerrar</button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getApiErrorMessage } from '@/util/apiError';
import { formatearFecha as fechaDeCalendario } from '@/util/fecha';
import SueldosAPI from '@/api/sueldos/sueldos';

export default {
  name: 'CondicionesLaboralesHome',
  data() {
    return {
      cargando: false,
      cargandoForm: false,
      modalVisible: false,
      error: null,
      empleados: [],
      editandoId: null,
      filtros: {
        empleado: '',
        vigentes: false,
      },
      form: {
        empleado: '',
        modalidad: 'FIJO',
        periodicidad: 'MENSUAL',
        importe_fijo: 0,
        horas_jornada_semanal: 44,
        valor_hora: null,
        redondeo_multiplo: 100,
        vigencia_desde: '',
        vigencia_hasta: '',
      },
    };
  },
  computed: {
    ...mapGetters('sueldos', ['condiciones']),
    condicionesFiltradas() {
      return this.condiciones.filter(cond => {
        if (this.filtros.empleado && cond.empleado !== parseInt(this.filtros.empleado)) {
          return false;
        }
        return true;
      });
    },
  },
  methods: {
    ...mapActions('sueldos', [
      'fetchCondiciones',
      'crearCondicion',
      'actualizarCondicion',
      'eliminarCondicion',
      'fetchCondicion',
    ]),
    async cargarCondiciones() {
      this.cargando = true;
      try {
        const vigentes = this.filtros.vigentes;
        await this.fetchCondiciones({ vigentes });
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargando = false;
      }
    },
    async cargarEmpleados() {
      try {
        this.empleados = await SueldosAPI.opciones.empleados();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
    },
    abrirModalCrear() {
      this.editandoId = null;
      this.form = {
        empleado: '',
        modalidad: 'FIJO',
        periodicidad: 'MENSUAL',
        importe_fijo: 0,
        horas_jornada_semanal: 44,
        valor_hora: null,
        redondeo_multiplo: 100,
        vigencia_desde: '',
        vigencia_hasta: '',
      };
      this.modalVisible = true;
    },
    async editarCondicion(id) {
      this.editandoId = id;
      try {
        const cond = await this.fetchCondicion(id);
        this.form = { ...cond.data };
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
      this.modalVisible = true;
    },
    async guardarCondicion() {
      this.cargandoForm = true;
      try {
        const data = { ...this.form };
        if (this.editandoId) {
          await this.actualizarCondicion({ id: this.editandoId, data });
        } else {
          await this.crearCondicion(data);
        }
        this.modalVisible = false;
        await this.cargarCondiciones();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargandoForm = false;
      }
    },
    async eliminarCondicion(id) {
      if (!confirm('¿Estás seguro de que deseas eliminar esta condición?')) {
        return;
      }
      try {
        await this.eliminarCondicion(id);
        await this.cargarCondiciones();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
    },
    formatearFecha(fecha) {
      return fechaDeCalendario(fecha);
    },
    formatearMoneda(valor) {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
      }).format(valor || 0);
    },
  },
  mounted() {
    this.cargarCondiciones();
    this.cargarEmpleados();
  },
  watch: {
    'filtros.vigentes': 'cargarCondiciones',
  },
};
</script>

<style scoped>
.condiciones-home {
  padding: 1rem;
}

.header {
  margin-bottom: 1.5rem;
}

.filters {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #9e9e9e;
}

.filter-group select {
  padding: 0.5rem 0.75rem;
  min-width: 160px;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 6px;
  color: #f5f5f5;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.filter-group select:hover {
  background-color: rgba(255, 255, 255, 0.09);
}

.filter-group select:focus {
  outline: none;
  border-color: #2196f3;
  background-color: rgba(255, 255, 255, 0.09);
}

.filter-group select option {
  background-color: #1a1a1a;
  color: #f5f5f5;
}

.filters > label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #d0d0d0;
  font-size: 0.9rem;
  padding-bottom: 0.6rem;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: #9e9e9e;
}

.condiciones-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.condiciones-table thead {
  background-color: rgba(255, 255, 255, 0.04);
}

.condiciones-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #f5f5f5;
  border-bottom: 2px solid rgba(255, 255, 255, 0.16);
}

.condiciones-table td {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.condiciones-table tr:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.numero {
  text-align: right;
  font-family: 'Courier New', monospace;
}

.acciones {
  text-align: center;
}

.btn-link {
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 0.95rem;
  margin: 0 0.5rem;
}

.btn-link:hover {
  color: #1565c0;
}

.btn-primary {
  padding: 0.5rem 1rem;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1565c0;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.5rem 1rem;
  background-color: rgba(255, 255, 255, 0.08);
  color: #f5f5f5;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.14);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #1e1e1e;
  color: #f5f5f5;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #9e9e9e;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  color: #f5f5f5;
  font-size: 0.95rem;
  font-family: inherit;
}

.form-group select option {
  background-color: #1a1a1a;
  color: #f5f5f5;
}

.form-group input[type="date"] {
  color-scheme: dark;
}

.form-group input:disabled {
  background-color: rgba(255, 255, 255, 0.03);
  color: #777;
  cursor: not-allowed;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.modal-footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.error-alert {
  padding: 1rem;
  background-color: rgba(244, 67, 54, 0.12);
  border: 1px solid rgba(244, 67, 54, 0.4);
  border-radius: 4px;
  color: #ff8a80;
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.error-alert button {
  background: none;
  border: none;
  color: #ff8a80;
  cursor: pointer;
  text-decoration: underline;
}
</style>
